import PermissionGate from '@/components/common/PermissionGate';
// ─────────────────────────────────────────────
// KLOTHIQ — Accounts Module Layout (TASK-FE-002)
// ─────────────────────────────────────────────

import { useState } from 'react';
import { Outlet, NavLink, useLocation, useSearchParams } from 'react-router-dom';
import Link from '@/components/common/AccessLink';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  Receipt,
  Wallet,
  FileSpreadsheet,
  Plus,
  ChevronDown,
  Landmark,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { permissionForPath } from '@klothiq/domain';
import { useCashAccounts, findKlothiqBank } from '@/hooks/useCash';
import PeriodSelector from './components/PeriodSelector';
import CapitalEntryModal from './components/CapitalEntryModal';
import ExpenseEntryModal from './components/ExpenseEntryModal';
import AdjustmentEntryModal from './components/AdjustmentEntryModal';
import PartnerFormModal from './components/PartnerFormModal';
import UpdateBankBalanceModal from './components/UpdateBankBalanceModal';
import {
  PeriodPreset,
  getPresetRange,
} from '@/utils/cashbookCalc';

export interface AccountsContextValue {
  from: string | null;
  to: string | null;
  preset: PeriodPreset;
  openCapitalModal: (partnerId?: string) => void;
  openExpenseModal: (categoryId?: string) => void;
  openAdjustmentModal: () => void;
  openPartnerModal: () => void;
  openBankBalanceModal: () => void;
  currentBankBalance: number;
}

export default function AccountsLayout() {
  const { can } = useAuth();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const isOwnerOrAdmin = ['accounts_capital.manage', 'accounts_expenses.manage', 'accounts_balances.manage'].some(can);

  // Read period from URL or default to 'month'
  const presetParam = (searchParams.get('preset') as PeriodPreset) || 'month';
  const defaultRange = getPresetRange(presetParam);
  const fromParam = searchParams.get('from') ?? defaultRange.from;
  const toParam = searchParams.get('to') ?? defaultRange.to;

  const handlePeriodChange = (range: {
    from: string | null;
    to: string | null;
    preset: PeriodPreset;
  }) => {
    const next = new URLSearchParams(searchParams);
    next.set('preset', range.preset);
    if (range.from) next.set('from', range.from);
    else next.delete('from');
    if (range.to) next.set('to', range.to);
    else next.delete('to');
    setSearchParams(next);
  };

  // Modals state
  const [isCapitalOpen, setIsCapitalOpen] = useState(false);
  const [isExpenseOpen, setIsExpenseOpen] = useState(false);
  const [isAdjustmentOpen, setIsAdjustmentOpen] = useState(false);
  const [isPartnerOpen, setIsPartnerOpen] = useState(false);
  const [isBankBalanceOpen, setIsBankBalanceOpen] = useState(false);
  const [targetPartnerId, setTargetPartnerId] = useState<string | undefined>();
  const [targetCategoryId, setTargetCategoryId] = useState<string | undefined>();
  const [isEntryMenuOpen, setIsEntryMenuOpen] = useState(false);

  // Accounts & KLOTHIQ Bank resolution (bank only — never the cash/UPI sales accounts)
  const { data: accounts } = useCashAccounts();
  const currentBankBalance = Number(findKlothiqBank(accounts)?.current_balance ?? 0);

  const openCapitalModal = (partnerId?: string) => {
    if (!can('accounts_capital.manage')) return;
    setTargetPartnerId(partnerId);
    setIsCapitalOpen(true);
  };

  const openExpenseModal = (categoryId?: string) => {
    if (!can('accounts_expenses.manage')) return;
    setTargetCategoryId(categoryId);
    setIsExpenseOpen(true);
  };

  const openAdjustmentModal = () => { if (can('accounts_balances.manage')) setIsAdjustmentOpen(true); };
  const openPartnerModal = () => { if (can('accounts_capital.manage')) setIsPartnerOpen(true); };
  const openBankBalanceModal = () => { if (can('accounts_balances.manage')) setIsBankBalanceOpen(true); };

  // Tab definitions
  const tabs = [
    { label: 'Overview', path: '/accounts', icon: <LayoutDashboard className="h-4 w-4" />, end: true },
    { label: 'Cashbook', path: '/accounts/cashbook', icon: <BookOpen className="h-4 w-4" /> },
    { label: 'Capital & Partners', path: '/accounts/capital', icon: <Users className="h-4 w-4" /> },
    { label: 'Expenses', path: '/accounts/expenses', icon: <Receipt className="h-4 w-4" /> },
    { label: 'Balances', path: '/accounts/balances', icon: <Wallet className="h-4 w-4" /> },
  ].filter((tab) => can(permissionForPath(tab.path)!));

  // Show date period selector on Overview, Cashbook, and Expenses
  const showPeriodSelector =
    location.pathname === '/accounts' ||
    location.pathname === '/accounts/cashbook' ||
    location.pathname === '/accounts/expenses';

  const contextValue: AccountsContextValue = {
    from: fromParam,
    to: toParam,
    preset: presetParam,
    openCapitalModal,
    openExpenseModal,
    openAdjustmentModal,
    openPartnerModal,
    openBankBalanceModal,
    currentBankBalance,
  };

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold tracking-tight text-foreground sm:text-2xl">
              Accounts & Cashbook
            </h1>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Financial ledger, cash in hand, partner equity and operational expenses
          </p>
        </div>

        {/* Global Action Buttons */}
        <div className="flex items-center gap-2">
          {isOwnerOrAdmin && (
            <>
              <PermissionGate permission="accounts_balances.manage"><button
                type="button"
                onClick={openBankBalanceModal}
                className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600/30 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-700 dark:text-emerald-400 shadow-xs hover:bg-emerald-500/20 transition-colors"
                title="Update available balance for KLOTHIQ Bank"
              >
                <Landmark className="h-3.5 w-3.5" />
                <span>Update Bank Balance</span>
              </button></PermissionGate>

              <Link
                to="/accounts/cashbook/import"
                className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground shadow-xs hover:bg-muted"
              >
                <FileSpreadsheet className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                <span>Import Sheet</span>
              </Link>
            </>
          )}

          {/* Quick Record Entry Dropdown (or Single Button for Staff) */}
          {isOwnerOrAdmin ? (
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsEntryMenuOpen(!isEntryMenuOpen)}
                className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-xs hover:bg-primary/90"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>+ Record Entry</span>
                <ChevronDown className="h-3 w-3 opacity-80" />
              </button>

              {isEntryMenuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-20"
                    onClick={() => setIsEntryMenuOpen(false)} data-field-guide-scope
                  />
                  <div className="absolute right-0 top-full z-30 mt-1 w-52 rounded-lg border border-border bg-card p-1 shadow-lg animate-in fade-in">
                    <PermissionGate permission="accounts_balances.manage"><button
                      type="button"
                      onClick={() => {
                        setIsEntryMenuOpen(false);
                        openBankBalanceModal();
                      }}
                      className="w-full text-left rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted flex items-center gap-2"
                    >
                      <Landmark className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                      Update Bank Balance
                    </button></PermissionGate>
                    <PermissionGate permission="accounts_capital.manage"><button
                      type="button"
                      onClick={() => {
                        setIsEntryMenuOpen(false);
                        openCapitalModal();
                      }}
                      className="w-full text-left rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted flex items-center gap-2"
                    >
                      <span className="h-2 w-2 rounded-full bg-primary" />
                      Capital Contribution
                    </button></PermissionGate>
                    <PermissionGate permission="accounts_expenses.manage"><button
                      type="button"
                      onClick={() => {
                        setIsEntryMenuOpen(false);
                        openExpenseModal();
                      }}
                      className="w-full text-left rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted flex items-center gap-2"
                    >
                      <span className="h-2 w-2 rounded-full bg-rose-500" />
                      Record Expense
                    </button></PermissionGate>
                    <PermissionGate permission="accounts_balances.manage"><button
                      type="button"
                      onClick={() => {
                        setIsEntryMenuOpen(false);
                        openAdjustmentModal();
                      }}
                      className="w-full text-left rounded-md px-2.5 py-1.5 text-xs font-medium text-foreground hover:bg-muted flex items-center gap-2"
                    >
                      <span className="h-2 w-2 rounded-full bg-slate-500" />
                      Cash Adjustment
                    </button></PermissionGate>
                  </div>
                </>
              )}
            </div>
          ) : (
            <PermissionGate permission="accounts_expenses.manage"><button
              type="button"
              onClick={() => openExpenseModal()}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground shadow-xs hover:bg-primary/90"
            >
              <Plus className="h-3.5 w-3.5" />
              <span>Record Expense</span>
            </button></PermissionGate>
          )}
        </div>
      </div>

      {/* Navigation Tabs Bar & Period Filter */}
      <div className="flex flex-col gap-3 border-b border-border sm:flex-row sm:items-center sm:justify-between pb-2">
        {/* Route Tabs */}
        <div className="flex overflow-x-auto gap-1">
          {tabs.map((tab) => (
            <NavLink
              key={tab.path}
              to={tab.path}
              end={tab.end}
              className={({ isActive }) =>
                `inline-flex items-center gap-2 border-b-2 px-3.5 py-2 text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive
                    ? 'border-primary text-primary font-semibold'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground'
                }`
              }
            >
              {tab.icon}
              {tab.label}
            </NavLink>
          ))}
        </div>

        {/* Period Selector (when applicable) */}
        {showPeriodSelector && (
          <PeriodSelector
            from={fromParam}
            to={toParam}
            preset={presetParam}
            onChange={handlePeriodChange}
          />
        )}
      </div>

      {/* Tab Page Outlet */}
      <Outlet context={contextValue} />

      {/* Modals */}
      <CapitalEntryModal
        isOpen={isCapitalOpen}
        onClose={() => setIsCapitalOpen(false)}
        defaultPartnerId={targetPartnerId}
        onOpenAddPartner={() => {
          setIsCapitalOpen(false);
          setIsPartnerOpen(true);
        }}
      />
      <ExpenseEntryModal
        isOpen={isExpenseOpen}
        onClose={() => setIsExpenseOpen(false)}
        defaultCategoryId={targetCategoryId}
      />
      <AdjustmentEntryModal
        isOpen={isAdjustmentOpen}
        onClose={() => setIsAdjustmentOpen(false)}
      />
      <PartnerFormModal
        isOpen={isPartnerOpen}
        onClose={() => setIsPartnerOpen(false)}
      />
      <UpdateBankBalanceModal
        isOpen={isBankBalanceOpen}
        onClose={() => setIsBankBalanceOpen(false)}
        currentBalance={currentBankBalance}
      />
    </div>
  );
}
